import type { TFunction } from 'i18next'
import {
  availableReqGuardTools,
  isVerbatimExcerpt,
  type AgentMode,
  type ReqGuardAction,
  type ReqGuardChatResponse,
} from '@cypremos/shared'
import { apiFetch } from '@/lib/apiFetch'
import type { ReqGuardBridgeHandle } from './chatActionBridgeContext'
import type { ChatBlock } from './types'

/**
 * The Requirement Doctor (/req-guard) side of the floating Chat: sending a turn to
 * /req-doctor/agent-chat and running the returned page action through the page bridge.
 * Kept apart from the vECU wizard code in useAssistantChat. See
 * docs/chat/phase-3-req-guard-chat-design.md.
 */

type Turn = { role: 'user' | 'assistant'; content: string }
type ActionBlock = Extract<ChatBlock, { kind: 'rgAction' }>
type ConfirmBlock = Extract<ChatBlock, { kind: 'rgConfirm' }>

export function isReqGuardResponse(response: object): response is ReqGuardChatResponse {
  return 'agent' in response && (response as { agent: unknown }).agent === 'req-guard'
}

export async function sendReqGuardTurn(
  handle: ReqGuardBridgeHandle,
  messages: Turn[],
  useMock: boolean,
  currentPath: string | undefined,
  t: TFunction,
): Promise<ReqGuardChatResponse> {
  const response = await apiFetch('/req-doctor/agent-chat', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ messages, snapshot: handle.getSnapshot(), useMock, currentPath }),
  })
  if (!response.ok) {
    // Most often Live mode without AWS credentials — say so instead of a generic failure.
    const body = (await response.json().catch(() => null)) as { message?: string; mode?: AgentMode; timeout?: boolean } | null
    if (!body) throw new Error(`HTTP error! status: ${response.status}`)
    return {
      agent: 'req-guard',
      intent: { type: 'none' },
      // A stalled AI call is worth resending; anything else is usually missing AWS credentials in Live.
      message: body.timeout ? t('chat.reqGuard.error.timeout') : t('chat.reqGuard.error.backend', { error: body.message ?? response.status }),
      mode: body.mode ?? (useMock ? 'mock' : 'live'),
      actions: [],
      rejectedActions: [],
      cards: [],
    }
  }
  return response.json()
}

/** The blocks attached to the assistant message; a page action starts as running (or a pending confirm card). */
export function reqGuardResponseBlocks(response: ReqGuardChatResponse): ChatBlock[] {
  return [
    ...response.rejectedActions.map((r): ChatBlock => ({ kind: 'rejected', name: r.name, reason: r.reason })),
    ...response.cards.map((card): ChatBlock => ({ kind: 'rgCard', card })),
    ...response.actions.map((action): ChatBlock =>
      action.name === 'propose_apply_coverage'
        ? { kind: 'rgConfirm', roundIndex: -1, acceptedCount: 0, status: 'pending' }
        : { kind: 'rgAction', action, status: 'running' }),
  ]
}

/** Re-check on the client, against the page as it is right now, before touching it. */
export function precheckReqGuardAction(
  action: ReqGuardAction,
  handle: ReqGuardBridgeHandle | null,
  userTexts: string[],
  t: TFunction,
): string | null {
  if (!handle) return t('chat.reqGuard.error.noPage')
  if (!availableReqGuardTools(handle.getSnapshot()).includes(action.name)) return t('chat.reqGuard.error.stateChanged')
  if (action.name === 'run_coverage' && action.source === 'message' && !userTexts.some(text => isVerbatimExcerpt(action.spec, text))) {
    return t('chat.agent.rejected.not_verbatim')
  }
  if (action.name === 'import_requirements' && action.source === 'message' && !userTexts.some(text => isVerbatimExcerpt(action.requirements, text))) {
    return t('chat.agent.rejected.not_verbatim')
  }
  return null
}

/** The confirm card for a page action that only proposes applying: bound to the current round. */
export function bindConfirm(handle: ReqGuardBridgeHandle): ConfirmBlock {
  const latest = handle.getSnapshot().coverage.latest
  return {
    kind: 'rgConfirm',
    roundIndex: latest?.roundIndex ?? -1,
    acceptedCount: latest?.proposals.filter(p => p.accepted).length ?? 0,
    status: 'pending',
  }
}

/** Runs one page action through the bridge and returns the finished block. */
export async function runReqGuardAction(action: ReqGuardAction, handle: ReqGuardBridgeHandle, block: ActionBlock, t: TFunction): Promise<ActionBlock> {
  try {
    switch (action.name) {
      case 'open_tab':
        handle.openTab(action.tab)
        return { ...block, status: 'done', detail: t('chat.reqGuard.action.openTabDone', { tab: t(`reqGuard.tabs.${action.tab}`) }) }
      case 'run_coverage': {
        const result = await handle.runCoverage(action.source === 'message' ? { spec: action.spec } : { sample: action.source === 'sample' })
        if (!result.ok) throw new Error(result.error)
        return { ...block, status: 'done', summary: result.summary }
      }
      case 'set_proposal_decisions': {
        const result = handle.setProposalDecisions(action.accept, action.reject)
        if (!result.ok) throw new Error(result.error)
        return { ...block, status: 'done', detail: t('chat.reqGuard.action.decisionsDone', { accepted: result.acceptedCount, total: result.proposalCount }) }
      }
      case 'propose_apply_coverage':
        // Rendered as a confirm card, never run here.
        return block
      case 'import_requirements': {
        const result = handle.importRequirements(action.source === 'message' ? { text: action.requirements } : { sample: true })
        if (!result.ok) throw new Error(result.error)
        return { ...block, status: 'done', detail: t('chat.reqGuard.action.importDone', { count: result.count }) }
      }
      case 'analyze_requirements': {
        const result = await handle.analyzeRequirements(action.ids)
        if (!result.ok) throw new Error(result.error)
        return { ...block, status: 'done', reviewSummary: result.summary }
      }
      case 'open_requirement':
      case 'start_fix': {
        const result = action.name === 'open_requirement' ? handle.openRequirement(action.id) : handle.startFix(action.id)
        if (!result.ok) throw new Error(result.error)
        return { ...block, status: 'done', detail: t(`chat.reqGuard.action.${action.name}Done`, { id: action.id }) }
      }
      case 'select_requirements':
      case 'propose_delete':
      case 'propose_force_approve': {
        const result = action.name === 'select_requirements'
          ? handle.selectRequirements(action.ids)
          : action.name === 'propose_delete'
            ? handle.openDeleteConfirmation(action.ids)
            : handle.openForceApproval(action.ids)
        if (!result.ok) throw new Error(result.error)
        return { ...block, status: 'done', detail: t(`chat.reqGuard.action.${action.name}Done`, { ids: action.ids.join(', '), count: action.ids.length }) }
      }
    }
  } catch (error) {
    return { ...block, status: 'failed', detail: error instanceof Error ? error.message : String(error) }
  }
}

/** A confirm card is stale once the page moved to another round, is busy, or has nothing accepted. */
export function isConfirmStale(block: ConfirmBlock, handle: ReqGuardBridgeHandle | null): boolean {
  const snapshot = handle?.getSnapshot()
  const latest = snapshot?.coverage.latest
  return !snapshot || snapshot.busy || !latest || latest.roundIndex !== block.roundIndex || !latest.proposals.some(p => p.accepted)
}
